/**
 * Navigation utilities using Shopify App Bridge Navigation API
 * Provides programmatic navigation within and outside the embedded app
 */

import type { NavigateOptions } from '../types/shopify-app-bridge';
import { logger } from './client-logger';

/**
 * Navigate to a URL using App Bridge
 * Supports internal app routes, Shopify admin pages, and external URLs
 *
 * @param destination - URL or Shopify admin path
 * @param options - Navigation options
 *
 * @example
 * ```ts
 * // Navigate within app
 * navigate('/app/settings');
 *
 * // Navigate to Shopify admin page
 * navigate('shopify://admin/products');
 *
 * // Open external URL in new tab
 * navigate('https://example.com', { target: '_blank' });
 * ```
 */
export function navigate(destination: string, options?: NavigateOptions): void {
  if (typeof window === 'undefined') return;

  // Try App Bridge first (preferred method for embedded apps)
  if (window.shopify?.navigate) {
    window.shopify.navigate(destination, options);
    return;
  }

  // App Bridge not available - handle fallback
  // For shopify://admin/* URLs, convert to HTTPS URLs using shop domain from URL params
  if (destination.startsWith('shopify://admin')) {
    const adminPath = destination.replace('shopify://admin', '');
    const shopDomain = new URLSearchParams(window.location.search).get('shop');

    if (shopDomain) {
      // Construct proper HTTPS URL to Shopify admin
      const adminUrl = `https://${shopDomain}/admin${adminPath}`;
      if (options?.target === '_blank') {
        window.open(adminUrl, '_blank');
      } else {
        // Open in new tab since we're leaving the embedded app context
        window.open(adminUrl, '_blank');
      }
      return;
    }

    // No shop domain available - log warning and fail silently
    logger.warn('Navigation', 'Cannot navigate to Shopify admin: shop domain not found in URL params');
    return;
  }

  // Regular URL fallback
  if (options?.target === '_blank') {
    window.open(destination, '_blank');
  } else {
    window.location.href = destination;
  }
}

/**
 * Navigate to a Shopify admin page
 * Uses the shopify://admin/... URL format
 *
 * @param path - Admin page path (e.g., '/products', '/customers')
 *
 * @example
 * ```ts
 * navigateToAdmin('/products'); // Goes to Products page
 * navigateToAdmin('/products/123456789'); // Goes to specific product
 * ```
 */
export function navigateToAdmin(path: string): void {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  navigate(`shopify://admin${cleanPath}`);
}

/**
 * Navigate to external URL
 * Always opens in new tab for security
 *
 * @param url - External URL
 *
 * @example
 * ```ts
 * navigateExternal('https://docs.stocksentry.com');
 * ```
 */
export function navigateExternal(url: string): void {
  navigate(url, { target: '_blank' });
}

/**
 * Navigate within the app with query parameters preserved
 *
 * @param path - Internal app path
 * @param searchParams - Current URLSearchParams
 * @param additionalParams - Additional params to add
 *
 * @example
 * ```ts
 * const [searchParams] = useSearchParams();
 * navigateWithParams('/app/settings', searchParams, { tab: 'alerts' });
 * ```
 */
export function navigateWithParams(
  path: string,
  searchParams: URLSearchParams,
  additionalParams?: Record<string, string>
): void {
  const params = new URLSearchParams(searchParams);

  if (additionalParams) {
    Object.entries(additionalParams).forEach(([key, value]) => {
      params.set(key, value);
    });
  }

  const queryString = params.toString();
  const destination = `${path}${queryString ? `?${queryString}` : ''}`;

  navigate(destination);
}

/**
 * Update URL without navigation using History API
 * Useful for updating query parameters or hash without reload
 *
 * @param url - New URL or path
 * @param replace - Whether to replace history entry (default: false)
 *
 * @example
 * ```ts
 * // Add query parameter without navigation
 * updateURL('/app?tab=alerts');
 *
 * // Replace current history entry
 * updateURL('/app?tab=velocity', true);
 * ```
 */
export function updateURL(url: string, replace: boolean = false): void {
  if (typeof window === 'undefined') return;

  if (replace) {
    window.history.replaceState({}, '', url);
  } else {
    window.history.pushState({}, '', url);
  }
}

/**
 * Common Shopify admin navigation shortcuts
 */
export const adminNavigation = {
  /**
   * Navigate to Products page
   */
  products: () => navigateToAdmin('/products'),

  /**
   * Navigate to specific product by ID
   */
  product: (productId: string) => {
    const id = productId.replace('gid://shopify/Product/', '');
    navigateToAdmin(`/products/${id}`);
  },

  /**
   * Navigate to Orders page
   */
  orders: () => navigateToAdmin('/orders'),

  /**
   * Navigate to specific order by ID
   */
  order: (orderId: string) => {
    const id = orderId.replace('gid://shopify/Order/', '');
    navigateToAdmin(`/orders/${id}`);
  },

  /**
   * Navigate to Customers page
   */
  customers: () => navigateToAdmin('/customers'),

  /**
   * Navigate to Analytics page
   */
  analytics: () => navigateToAdmin('/analytics'),

  /**
   * Navigate to Settings
   */
  settings: () => navigateToAdmin('/settings'),
};

/**
 * Check if navigation is available via App Bridge
 */
export function isNavigationAvailable(): boolean {
  return typeof window !== 'undefined' && window.shopify?.navigate !== undefined;
}
